import { styled } from '@mui/material/styles';
import { Switch, FormControlLabel } from '@mui/material';
import { SwitchSize, SwitchValidationState } from './Switch.types';
import { designTokens } from '../../tokens';

/**
 * Size-specific styles for switches
 */
const getSizeStyles = (size: SwitchSize) => {
  switch (size) {
    case 'sm':
      return {
        '& .MuiSwitch-switchBase': {
          padding: '4px',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
          },
        },
        '& .MuiSwitch-thumb': {
          width: 12,
          height: 12,
        },
        '& .MuiSwitch-track': {
          borderRadius: 10,
          width: 32,
          height: 20,
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.xs,
        },
      };
    case 'md':
      return {
        '& .MuiSwitch-switchBase': {
          padding: '6px',
          '&.Mui-checked': {
            transform: 'translateX(20px)',
          },
        },
        '& .MuiSwitch-thumb': {
          width: 16,
          height: 16,
        },
        '& .MuiSwitch-track': {
          borderRadius: 14,
          width: 42,
          height: 28,
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.sm,
        },
      };
    case 'lg':
      return {
        '& .MuiSwitch-switchBase': {
          padding: '8px',
          '&.Mui-checked': {
            transform: 'translateX(24px)',
          },
        },
        '& .MuiSwitch-thumb': {
          width: 20,
          height: 20,
        },
        '& .MuiSwitch-track': {
          borderRadius: 18,
          width: 52,
          height: 36,
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.base,
        },
      };
    default:
      return {};
  }
};

/**
 * Validation state styles
 */
const getValidationStyles = (validationState: SwitchValidationState) => {
  const tokens = designTokens.colors;

  switch (validationState) {
    case 'error':
      return {
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: tokens.error[500],
          '& + .MuiSwitch-track': {
            backgroundColor: tokens.error[500],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.error[700],
        },
      };

    case 'success':
      return {
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: tokens.success[500],
          '& + .MuiSwitch-track': {
            backgroundColor: tokens.success[500],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.success[700],
        },
      };

    case 'warning':
      return {
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: tokens.warning[500],
          '& + .MuiSwitch-track': {
            backgroundColor: tokens.warning[500],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.warning[700],
        },
      };

    case 'default':
    default:
      return {
        '& .MuiSwitch-switchBase': {
          color: tokens.neutral[300],
          '&.Mui-checked': {
            color: tokens.primary[500],
            '& + .MuiSwitch-track': {
              backgroundColor: tokens.primary[500],
              opacity: 0.7,
            },
          },
          '&:hover': {
            backgroundColor: `${tokens.primary[50]}40`,
          },
        },
        '& .MuiSwitch-track': {
          backgroundColor: tokens.neutral[400],
          opacity: 0.5,
        },
      };
  }
};

/**
 * Styled Switch component
 */
export const StyledSwitch = styled(Switch, {
  shouldForwardProp: (prop) =>
    !['switchSize', 'validationState'].includes(prop as string),
})<{
  switchSize: SwitchSize;
  validationState: SwitchValidationState;
}>(({ switchSize, validationState }) => ({
  ...getSizeStyles(switchSize),
  ...getValidationStyles(validationState),

  // Base styles
  padding: 0,

  '& .MuiSwitch-switchBase': {
    transition: designTokens.transitions.duration.normal,

    '&.Mui-disabled': {
      color: designTokens.colors.neutral[300],
      '& + .MuiSwitch-track': {
        backgroundColor: designTokens.colors.neutral[300],
        opacity: 0.3,
      },
    },
  },

  '& .MuiSwitch-thumb': {
    boxShadow: designTokens.shadows.sm,
    backgroundColor: '#fff',
  },

  '& .MuiSwitch-track': {
    borderRadius: 14,
    transition: `background-color ${designTokens.transitions.duration.fast}`,
  },
}));

/**
 * Styled FormControlLabel wrapper
 */
export const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) =>
    !['switchSize', 'validationState'].includes(prop as string),
})<{
  switchSize: SwitchSize;
  validationState: SwitchValidationState;
}>(({ switchSize, validationState }) => ({
  ...getSizeStyles(switchSize),
  ...getValidationStyles(validationState),

  margin: 0,
  fontFamily: designTokens.typography.fontFamily.primary,

  '& .MuiFormControlLabel-label': {
    fontWeight: designTokens.typography.fontWeight.regular,
    lineHeight: designTokens.typography.lineHeight.normal,
    userSelect: 'none',
  },

  '&.Mui-disabled .MuiFormControlLabel-label': {
    color: designTokens.colors.neutral[400],
  },
}));
